import { toast } from 'sonner';

import { useAxios } from '@/hooks/useAxios';
import { BulkSelectionState } from '@/hooks/useBulkSelection/types';
import { useBulkJobsStore } from '@/state/bulkJobs.store';
import { useMutation } from '@/utils/api';

import { BulkActionPayload, BulkJobResponse } from './types';

/**
 * Number of selected items above which the bulk endpoint is used
 * instead of per-item mutations (Promise.allSettled).
 *
 * - manual mode with keys.length <= BULK_THRESHOLD → per-item path
 * - manual mode with keys.length > BULK_THRESHOLD → /bulk endpoint
 * - 'all' mode → always /bulk endpoint
 */
export const BULK_THRESHOLD = 2;

/**
 * Determines whether the bulk endpoint should be used for a given selection state.
 *
 * Returns true when:
 * - Action is 'remove_profile' (direct PUT /asset {profile: ''} is broken —
 *   MergeFields skips empty-string assignments; bulk worker uses aws.Graph.Insert
 *   to bypass merge. See feature/scan-profile-removal commit b155aa856.), OR
 * - Selection is in 'all' mode (filter-based), OR
 * - Manual mode with more than BULK_THRESHOLD keys selected
 */
export function shouldUseBulk(
  state: BulkSelectionState,
  action?: string
): boolean {
  if (action === 'remove_profile') return true; // bypass threshold; PUT path is broken for clears
  if (state.mode === 'all') return true;
  return state.keys.length > BULK_THRESHOLD;
}

export interface UseBulkActionParams {
  /** Resource type (asset, risk, seed, technology) */
  resource: string;
  /** Action name (update_status, tag, delete, export) */
  action: string;
}

/**
 * useBulkAction wraps useMutation to POST bulk action requests to `/bulk/{resource}`.
 *
 * Shows a single success toast on submission. The exact count appears in the
 * job result (accessible via the Jobs table).
 *
 * @example
 * ```tsx
 * const { mutate: submitBulk, isPending } = useBulkAction({ resource: 'asset', action: 'update_status' });
 *
 * const handleBulkAction = (selectionState: BulkSelectionState) => {
 *   if (shouldUseBulk(selectionState)) {
 *     submitBulk({
 *       action: 'update_status',
 *       params: { status: 'F' },
 *       selection: selectionState.mode === 'all'
 *         ? { mode: 'all', filters: selectionState.filters, excludedKeys: selectionState.excludedKeys }
 *         : { mode: 'manual', keys: selectionState.keys },
 *     });
 *   }
 * };
 * ```
 */
export function useBulkAction({ resource, action }: UseBulkActionParams) {
  const axios = useAxios({});
  const addJob = useBulkJobsStore(state => state.addJob);

  const actionLabel =
    action.charAt(0).toUpperCase() + action.slice(1).replace(/_/g, ' ');

  return useMutation<BulkJobResponse, Error, BulkActionPayload>({
    defaultErrorMessage: `Failed to ${action} ${resource}s`,
    mutationFn: async (payload: BulkActionPayload) => {
      const { data } = await axios.post<BulkJobResponse>(
        `/bulk/${resource}`,
        payload
      );
      return data;
    },
    onSuccess: (data, payload) => {
      // Register job in store with explicit pending keys (or empty for query mode).
      // submittedAt is captured inside addJob as Date.now().
      // UX divergence #3: markSuppressedIfFast will silence badge for sub-2s jobs.
      const pendingKeys =
        payload.selection.mode === 'explicit'
          ? new Set(payload.selection.keys)
          : new Set<string>();
      addJob(data.job.key, { resource, action, pendingKeys });

      // UX divergence #4: emit submit toast only — NO completion toast from this hook.
      // Success is communicated by badge behavior, drawer entry, and row refetch.
      toast.info(`${actionLabel} submitted`, {
        description: 'Track progress in the bulk actions icon (top-right).',
        duration: 5000,
      });
    },
    onError: () => {
      toast.error(`${actionLabel} failed`, {
        description:
          'Please try again. If the problem persists, contact support@praetorian.com.',
        duration: 8000,
      });
    },
  });
}
